"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.UniformInfiniteScroll = void 0;
var _jsxRuntime = require("@emotion/react/jsx-runtime");
var React = _interopRequireWildcard(require("react"));
var _assert = require("wnd-util/lib/assert");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); } //
// This is adapted from the react-list (https://github.com/caseywebdev/react-list)
// project, original license below:
// The MIT License (MIT)
//
// Copyright (c) 2017 Casey Foster
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
// the Software, and to permit persons to whom the Software is furnished to do so,
// subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
// FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
// COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
// IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
// CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
// eslint-disable-next-line
const NOOP = () => {};
// If a browser doesn't support the `options` argument to
// add/removeEventListener, we need to check, otherwise we will
// accidentally set `capture` with a truthy value.
const PASSIVE = (() => {
  if (typeof window === 'undefined') return false;
  let hasSupport = false;
  try {
    document.createElement('div').addEventListener('test', NOOP, {
      get passive() {
        hasSupport = true;
        return false;
      }
    });
  } catch (e) {
    // noop
  }
  return hasSupport;
})() ? {
  passive: true
} : false;
// Don't really need to support X yet... or ever probably
var Axis;
(function (Axis) {
  // X,
  Axis[Axis["Y"] = 0] = "Y";
})(Axis || (Axis = {}));
function isWindow(element) {
  return element === window;
}
const defaultScrollParentGetter = element => {
  let parent = element.parentElement;
  while (parent) {
    switch (window.getComputedStyle(parent).overflowY) {
      case 'auto':
      case 'scroll':
      case 'overlay':
        return parent;
    }
    parent = parent.parentElement;
  }
  return window;
};
function getOffset(element) {
  if (isWindow(element)) {
    return 0;
  }
  let offset = element.clientTop;
  do {
    offset += element.offsetTop ?? 0;
    element = element.offsetParent;
  } while (element);
  return offset;
}
class ScrollParent {
  constructor(scrollParent, invalidationEvent) {
    _defineProperty(this, "scrollParent", void 0);
    _defineProperty(this, "invalidationEvent", void 0);
    this.scrollParent = scrollParent;
    this.invalidationEvent = invalidationEvent;
    scrollParent.addEventListener('mousewheel', NOOP, PASSIVE);
    scrollParent.addEventListener('scroll', invalidationEvent, PASSIVE);
  }
  get element() {
    return this.scrollParent;
  }
  getScrollTop() {
    if (isWindow(this.scrollParent)) {
      // Firefox always returns document.body[scrollKey] as 0 and Chrome/Safari
      // always return document.documentElement[scrollKey] as 0, so take
      // whichever has a value.
      return document.body.scrollTop || document.documentElement.scrollTop;
    } else {
      return this.scrollParent.scrollTop;
    }
  }
  getScrollSize() {
    if (isWindow(this.scrollParent)) {
      return Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    } else {
      return this.scrollParent.scrollHeight;
    }
  }
  getViewportSize() {
    if (isWindow(this.scrollParent)) {
      return this.scrollParent.innerHeight;
    } else {
      return this.scrollParent.clientHeight;
    }
  }
  getScrollPosition() {
    const actual = this.getScrollTop();
    const max = this.getScrollSize() - this.getViewportSize();
    return Math.max(0, Math.min(actual, max));
  }
  getOffset() {
    return getOffset(this.scrollParent);
  }
  unmount() {
    this.scrollParent.removeEventListener('mousewheel', NOOP, PASSIVE);
    this.scrollParent.removeEventListener('scroll', this.invalidationEvent, PASSIVE);
  }
}
function calculateVisibleRange(scrollParent, container, threshold, listItemCount) {
  const scrollPosition = scrollParent.getOffset() + scrollParent.getScrollPosition() - getOffset(container);
  const start = Math.max(0, scrollPosition - threshold);
  const end = scrollPosition + scrollParent.getViewportSize() + threshold;
  return [start, end];
}
const UniformInfiniteScroll = ({
  threshold = 100,
  listItemCount = 0,
  minSize = 1,
  itemsRenderer,
  itemRenderer,
  visibleRowsUpdate
}) => {
  (0, _assert.assert)(minSize >= 1, 'Min size must be at least 1 so we can calculate the row height.');
  const containerRef = React.useRef();
  const scrollParentRef = React.useRef();
  const itemHeightRef = React.useRef(0);
  const startRowIndex = React.useRef(0);
  const visibleRowCount = React.useRef(0);
  const [nonVisibleBuffer, setNonVisibleBuffer] = React.useState([0, 0]);
  const getItemOffset = React.useCallback(index => {
    return index * itemHeightRef.current;
  }, []);
  // Includes the threshold
  const getVisibleRange = React.useCallback(() => {
    (0, _assert.assertExists)(scrollParentRef.current, 'Scroll parent must exist');
    (0, _assert.assertExists)(containerRef.current, 'Container must exist');
    const [start, end] = calculateVisibleRange(scrollParentRef.current, containerRef.current, threshold, listItemCount);
    return [start, Math.min(end, getItemOffset(listItemCount))];
  }, [threshold, listItemCount]);
  const recalculateVisibility = React.useCallback(() => {
    // The original implementation recalculated the scrollParent here
    // but we can elide that functionality until we need it.
    const itemHeight = itemHeightRef.current;
    if (scrollParentRef.current && containerRef.current && itemHeight > 0) {
      const [start, end] = getVisibleRange();
      const firstItemShown = Math.floor(start / itemHeight);
      const lastItemShown = Math.ceil(end / itemHeight);
      const priorBuffer = firstItemShown * itemHeight;
      const afterBuffer = (listItemCount - lastItemShown) * itemHeight;
      if (visibleRowsUpdate) {
        visibleRowsUpdate(firstItemShown, lastItemShown);
      }
      startRowIndex.current = firstItemShown;
      visibleRowCount.current = lastItemShown - firstItemShown;
      setNonVisibleBuffer([priorBuffer, afterBuffer]);
    }
  }, [visibleRowsUpdate, getVisibleRange]);
  const invalidationEvent = React.useCallback(() => {
    recalculateVisibility();
  }, [recalculateVisibility]);
  React.useEffect(() => {
    window.addEventListener('resize', invalidationEvent);
    return () => {
      window.removeEventListener('resize', invalidationEvent);
    };
  }, [invalidationEvent]);
  const updateContainer = React.useCallback(container => {
    if (scrollParentRef.current) {
      scrollParentRef.current.unmount();
    }
    if (container) {
      scrollParentRef.current = new ScrollParent(defaultScrollParentGetter(container), invalidationEvent);
    } else {
      scrollParentRef.current = undefined;
    }
    containerRef.current = container ?? undefined;
    recalculateVisibility();
  }, [recalculateVisibility, invalidationEvent]);
  const updateRowSize = React.useCallback(el => {
    if (el) {
      const prevHeight = itemHeightRef.current;
      itemHeightRef.current = el.offsetHeight;
      if (prevHeight !== itemHeightRef.current) {
        recalculateVisibility();
      }
    }
  }, [recalculateVisibility]);
  const realItemCount = Math.max(Math.min(minSize, listItemCount), visibleRowCount.current); //, listItemCount);
  const items = new Array(realItemCount);
  for (let i = 0; i < realItemCount; i += 1) {
    items[i] = itemRenderer(i + startRowIndex.current, i === 0 ? itemHeightRef.current === 0 ? updateRowSize : NOOP : NOOP);
  }
  const [above, below] = nonVisibleBuffer;
  const output = itemsRenderer(updateContainer, (0, _jsxRuntime.jsx)(_jsxRuntime.Fragment, {
    children: items
  }), above, below);
  return (0, _jsxRuntime.jsx)(_jsxRuntime.Fragment, {
    children: output
  });
};
exports.UniformInfiniteScroll = UniformInfiniteScroll;